import { Col, DatePicker, Row } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import Table, { ColumnsType } from "antd/es/table";
import { FormattedMessage, useIntl } from "react-intl";
import { DictionaryString } from "../../../../utils/types/generalTypes";
import { SunkhronosCompany, SunkhronosCompanyUsage as SunkhronosCompanyUsageType } from "../../../../utils/types/applications/sunkhronosTypes";
import getFormat from "../../../../utils/lang";
import ColumnInfo from "../../../common/ColumnInfo/ColumnInfo";
import CircleButton from "../../../common/CircleButton/CircleButton";
import FAIcon from "../../../common/FAIcon/FAIcon";
import Spinner from "../../../common/Spinner/Spinner";
import VirtualTable from "../../../common/VirtualTable/VirtualTable";
import { NivoLinechartData, NivoLinechartDataDataset } from "../../../common/Chart/LineChart/types";
import LineChart from "../../../common/Chart/LineChart/LineChart";
import { useViewAddons } from "../../../common/Layout/ViewAddons/ViewAddons";
import Fieldset from "../../../common/Fieldset/Fieldset";
import { SidebarEnum } from "../../../common/Layout/ViewAddons/ViewAddonsRenderer";
import { Filter } from "../../../common/Filter/Filter";
import EditCompanyContent from "../../drawers/editCompany/EditCompanyContent";

interface Props {
    company?: SunkhronosCompany;
    usage: DictionaryString<SunkhronosCompanyUsageType>;
    loading: boolean;
    startDate: Dayjs;
    endDate: Dayjs;
    setStartDate: (date: Dayjs) => void;
    setEndDate: (date: Dayjs) => void;
    refreshUsage: () => void;
    setCompany: (company: SunkhronosCompany) => void;
}

interface TableSunkhronosCompanyUsage {
    year: string;
    month: string;
    data: SunkhronosCompanyUsageType;
}

const SunkhronosCompanyUsage = (props: Props) => {
    const intl = useIntl();
    const viewAddons = useViewAddons();
    const [displayChart, setDisplayChart] = useState(false);

    // #region Memo
    const dataSource = useMemo(() => {
        if (displayChart) {
            const dates: string[] = [];

            let tmpDate = props.startDate.clone();
            while (props.endDate.isAfter(tmpDate)) {
                dates.push(`${tmpDate.month() + 1}/${tmpDate.year()}`);
                tmpDate = tmpDate.add(1, 'month');
            }
            dates.push(`${tmpDate.month() + 1}/${tmpDate.year()}`);

            const activeUsers: NivoLinechartDataDataset[] = [];
            const users: NivoLinechartDataDataset[] = [];
            const inactiveUsers: NivoLinechartDataDataset[] = [];
            const events: NivoLinechartDataDataset[] = [];
            const messages: NivoLinechartDataDataset[] = [];
            const posts: NivoLinechartDataDataset[] = [];
            const usersWithEvents: NivoLinechartDataDataset[] = [];
            const usersWithEventClocked: NivoLinechartDataDataset[] = [];
            const plannedEvents: NivoLinechartDataDataset[] = [];
            const plannedUsersWithEvents: NivoLinechartDataDataset[] = [];

            Object.keys(props.usage).map((item, idx) => {
                const data = props.usage[item];
                activeUsers.push({ x: dates[idx], y: data.activeUsers });
                users.push({ x: dates[idx], y: data.users });
                inactiveUsers.push({ x: dates[idx], y: data.users - data.activeUsers });
                events.push({ x: dates[idx], y: data.events });
                usersWithEvents.push({ x: dates[idx], y: data.usersWithEvents });
                usersWithEventClocked.push({ x: dates[idx], y: data.usersWithEventClocked });
                messages.push({ x: dates[idx], y: data.messages });
                posts.push({ x: dates[idx], y: data.posts });
                plannedEvents.push({ x: dates[idx], y: data.plannedEvents });
                plannedUsersWithEvents.push({ x: dates[idx], y: data.plannedUsersWithEvents });
            });

            return [
                {
                    id: intl.formatMessage({ defaultMessage: 'Users with events clocked' }),
                    key: 'users_with_event_clocked',
                    data: usersWithEventClocked
                },
                {
                    id: intl.formatMessage({ defaultMessage: 'Users with events' }),
                    key: 'users_with_event',
                    data: usersWithEvents
                },
                {
                    id: intl.formatMessage({ defaultMessage: 'Inactive users' }),
                    key: 'inactive_users',
                    data: inactiveUsers
                },
                {
                    id: intl.formatMessage({ defaultMessage: 'Active users' }),
                    key: 'active_users',
                    data: activeUsers
                },
                {
                    id: intl.formatMessage({ defaultMessage: 'Users' }),
                    key: 'users',
                    data: users
                },
                // {
                //     id: 'Planned events',
                //     data: plannedEvents
                // },
                // {
                //     id: 'Planned users with events',
                //     data: plannedUsersWithEvents
                // },
            ];
        }
        else {
            return Object.keys(props.usage).map((item) => ({ year: item.split('-')[0], month: item.split('-')[1], data: props.usage[item] }));
        }
    }, [displayChart, intl, props.endDate, props.startDate, props.usage]);

    const columns = useMemo((): ColumnsType<TableSunkhronosCompanyUsage> => {
        const calculateRowSpan = (data: TableSunkhronosCompanyUsage[], index: number): number => {
            if (index === 0 || data[index].year !== data[index - 1].year) {
                let span = 1;
                for (let i = index + 1; i < data.length; i++) {
                    if (data[i].year === data[index].year) {
                        span++;
                    } else {
                        break;
                    }
                }
                return span;
            }
            return 0; // Hide subsequent cells
        };

        return [
            {
                key: 'year',
                dataIndex: 'year',
                title: <FormattedMessage defaultMessage={'Year'} />,
                align: 'center',
                className: 'w-150',
                onCell: (_, index) => {
                    const rowSpan = calculateRowSpan(dataSource as TableSunkhronosCompanyUsage[], index!); // `index` is passed from Antd internally
                    return {
                        rowSpan,
                    };
                },
            },
            {
                key: 'month',
                dataIndex: 'month',
                title: <FormattedMessage defaultMessage={'Month'} />,
                className: 'w-150',
                render: (_, record) => dayjs(record.month, 'MM').format(getFormat("MONTH"))
            },
            {
                key: 'users',
                dataIndex: 'users',
                title: <FormattedMessage defaultMessage={'Users'} />,
                align: 'center',
                className: 'w-150',
                render: (_, record) => record.data.users
            },
            {
                key: 'events',
                dataIndex: 'events',
                title: <ColumnInfo title={<FormattedMessage defaultMessage={'Events'} />}
                    popoverContent={
                        <>
                            <p><FormattedMessage defaultMessage={'Represents the number of events in the company.'} /></p>
                            <p><FormattedMessage defaultMessage={'If the displayed month is in the future, the amount represents the number of events planned for that month. It is also displayed in orange.'} /></p>
                        </>
                    }
                />,
                align: 'center',
                className: 'w-150',
                render: (_, record) => record.data.hasUsage ? record.data.events : <span className={'usage-prevision'}>{record.data.plannedEvents}</span>
            },
            {
                key: 'usage',
                dataIndex: 'usage',
                title: <FormattedMessage defaultMessage={'Usage'} />,
                children: [

                    {
                        key: 'activeUsers',
                        dataIndex: 'activeUsers',
                        title: <FormattedMessage defaultMessage={'Active users'} />,
                        align: 'center',
                        className: 'w-250',
                        render: (_, record) => record.data.activeUsers
                    },
                    {
                        key: 'usersWithEvents',
                        dataIndex: 'usersWithEvents',
                        title: <ColumnInfo title={<FormattedMessage defaultMessage={'Users with events'} />}
                            popoverContent={
                                <>
                                    <p><FormattedMessage defaultMessage={'Represents the number of users with events in the company.'} /></p>
                                    <p><FormattedMessage defaultMessage={'If the displayed month is in the future, the amount represents the number of users with events planned for that month. It is also displayed in orange.'} /></p>
                                </>
                            }
                        />,
                        align: 'center',
                        className: 'w-250',
                        render: (_, record) => record.data.hasUsage ? record.data.usersWithEvents : <span className={'usage-prevision'}>{record.data.plannedUsersWithEvents}</span>
                    },
                    {
                        key: 'usersWithEventClocked',
                        dataIndex: 'usersWithEventClocked',
                        title: <FormattedMessage defaultMessage={'Users with events clocked'} />,
                        align: 'center',
                        className: 'w-250',
                        render: (_, record) => record.data.usersWithEventClocked
                    },

                ]
            },
        ];
    }, [dataSource]);

    const updateViewAddonsSidebar = useCallback(() => {
        viewAddons.setSidebars([
            {
                button: {
                    title: intl.formatMessage({ defaultMessage: 'Filters' }),
                    icon: <FAIcon prefix='fad' name='filters' />
                },
                type: SidebarEnum.FILTERS,
                content: (
                    <Row>
                        <Col xs={{ span: 24 }}>
                            <Fieldset legend={<><FAIcon prefix="fad" name="chart-line" /> <FormattedMessage defaultMessage={'Usage'} /></>}>
                                <Filter
                                    title={<FormattedMessage defaultMessage={'Usage date'} />}
                                    input={
                                        <DatePicker.RangePicker
                                            picker='month'
                                            style={{ width: '100%' }}
                                            value={[props.startDate, props.endDate]}
                                            allowClear={false}
                                            disabled={props.loading}
                                            onChange={(e) => {
                                                if (e) {
                                                    if (e[0])
                                                        props.setStartDate(e[0].startOf('month'));
                                                    if (e[1])
                                                        props.setEndDate(e[1].endOf('month'));
                                                }
                                            }}
                                        />
                                    }
                                />
                            </Fieldset>
                        </Col>
                    </Row>
                )
            },
            {
                button: {
                    title: intl.formatMessage({ defaultMessage: 'Configuration' }),
                    icon: <FAIcon prefix='fad' name='gear' />
                },
                type: SidebarEnum.SETTINGS,
                content: (
                    <>
                        <EditCompanyContent
                            cancelText={<FormattedMessage defaultMessage={'Reset'} />}
                            selectedCompany={props.company}
                            selectedDate={props.startDate}
                            onCancel={(company) => {
                                if (company)
                                    props.setCompany(company);
                            }}
                        />
                    </>
                )
            }
        ]);
    }, [intl, props, viewAddons]);

    const updateViewAddonsExtra = useCallback(() => {
        viewAddons.setExtra([
            <CircleButton
                icon={<FAIcon prefix='fad' name='rotate' />}
                onClick={props.refreshUsage}
                loading={props.loading}
                title={intl.formatMessage({ defaultMessage: 'Refresh usage data' })}
            />,
            <CircleButton
                icon={<FAIcon prefix='fad' name='chart-line' />}
                onClick={() => setDisplayChart(!displayChart)}
                type={displayChart ? 'primary' : 'default'}
                title={intl.formatMessage({ defaultMessage: 'Display chart' })}
            />
        ]);
    }, [displayChart, intl, props.loading, props.refreshUsage, viewAddons]);

    useEffect(() => {
        updateViewAddonsSidebar();
        updateViewAddonsExtra();
    }, [updateViewAddonsSidebar, updateViewAddonsExtra]);
    // #endregion

    // #region Render
    return (
        <>
            {
                displayChart ?
                    props.loading ?
                        <Spinner />
                        :
                        <LineChart data={dataSource as NivoLinechartData[]} />
                    :
                    <VirtualTable
                        height={350}
                        loading={{
                            spinning: props.loading,
                            indicator: <FAIcon prefix='fad' name='spinner-third' style={{ width: 50, height: 50 }} spin />
                        }}
                        columns={columns}
                        dataSource={dataSource as TableSunkhronosCompanyUsage[]}
                        pagination={false}
                        summary={(pageData) => {
                            let totalEvents = 0;
                            let totalPlannedEvents = 0;
                            let totalActiveUsers = 0;
                            let totalUsersWithEvents = 0;
                            let totalPlannedUsersWithEvents = 0;
                            let totalUsersWithEventClocked = 0;

                            const nbMonths = props.endDate.diff(props.startDate, 'months') + 1;

                            const average = (num: number, months = nbMonths) => {
                                if (months != 0) {
                                    return (num / months).toFixed(2);
                                }
                                return 0;
                            };


                            pageData.forEach(({ data }) => {
                                totalEvents += data.events;
                                totalPlannedEvents += data.hasUsage ? data.events : data.plannedEvents;
                                totalActiveUsers += data.activeUsers;
                                totalUsersWithEvents += data.usersWithEvents;
                                totalPlannedUsersWithEvents += data.hasUsage ? data.usersWithEvents : data.plannedUsersWithEvents;
                                totalUsersWithEventClocked += data.usersWithEventClocked;
                            });

                            return (
                                <Table.Summary fixed>

                                    <Table.Summary.Row className='usage-summary' style={{ fontWeight: 'bold', color: 'var(--primary)' }}>
                                        <Table.Summary.Cell index={0} align='center'><FormattedMessage defaultMessage='Total' /></Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} />
                                        <Table.Summary.Cell index={2} />
                                        <Table.Summary.Cell index={3} align='center'>
                                            <span>
                                                {totalEvents}
                                                <i style={{ color: 'var(--warning-dark)' }}>{`(${totalPlannedEvents})`}</i>
                                            </span>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={4} align='center'>{totalActiveUsers} <FAIcon prefix='fad' name='user' /> </Table.Summary.Cell>
                                        <Table.Summary.Cell index={5} align='center'>{totalUsersWithEvents}
                                            <i style={{ color: 'var(--warning-dark)' }}> ({totalPlannedUsersWithEvents}) </i><FAIcon prefix='fad' name='calendar' /></Table.Summary.Cell>
                                        <Table.Summary.Cell index={6} align='center'>{totalUsersWithEventClocked} <FAIcon prefix='fad' name='timer' /></Table.Summary.Cell>
                                    </Table.Summary.Row>

                                    <Table.Summary.Row className='usage-summary' style={{ fontWeight: 'bold', color: 'var(--primary)' }}>
                                        <Table.Summary.Cell index={0} align='center'><FormattedMessage defaultMessage='Average' /></Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} />
                                        <Table.Summary.Cell index={2} />
                                        <Table.Summary.Cell index={3} align='center'>{average(totalEvents)} <i style={{ color: 'var(--warning-dark)' }}>({average(totalPlannedEvents)})</i></Table.Summary.Cell>
                                        <Table.Summary.Cell index={4} align='center'>{average(totalActiveUsers)} </Table.Summary.Cell>
                                        <Table.Summary.Cell index={5} align='center'>{average(totalUsersWithEvents)}
                                            <i style={{ color: 'var(--warning-dark)' }}> ({average(totalPlannedUsersWithEvents)}) </i></Table.Summary.Cell>
                                        <Table.Summary.Cell index={6} align='center'>{average(totalUsersWithEventClocked)} </Table.Summary.Cell>
                                    </Table.Summary.Row>

                                </Table.Summary>
                            );
                        }}
                    />
            }
        </>
    );
    // #endregion
};

export default SunkhronosCompanyUsage;